import { Observable, of, switchMap, tap } from 'rxjs';
import { AuthServiceStrategy } from './auth-service-strategy';
import { LoginRequest } from '../../models/requests/login-request';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { LocalStorageService } from '../../services/local-storage.service';
import { AuthType } from '../../shared/enums/AuthType.enum';
import { PermissionService } from '../permissions/permission.service';
import { resetGlobalRulesStates } from '../../stores/global';
import {
  getLocalBaseApiUrl,
  getPosApiUrl,
  getPosMiddlewareAuthApiUrl,
} from '../../shared/utils/api-url.util';

@Injectable({
  providedIn: 'root',
})
export class BasicAuthService implements AuthServiceStrategy {
  readonly type = AuthType.BASIC;
  readonly baseUrl = getLocalBaseApiUrl();
  private readonly authApiUrl = getPosMiddlewareAuthApiUrl(true);
  private readonly posApiUrl = getPosApiUrl(true);

  private readonly http = inject(HttpClient);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(PermissionService);
  private readonly store = inject(Store);

  buildAuthHeader(): string | null {
    const accessToken = this.localStorageService.getAccessToken();
    if (!accessToken) {
      return null;
    }
    return `${AuthType.BASIC} ${accessToken}`;
  }

  login(request: LoginRequest): Observable<any> {
    return this.http
      .post<any>(`${this.authApiUrl}/login`, request)
      .pipe(
        tap((res) => {
          this.store.dispatch(resetGlobalRulesStates());
          const token = res?.data?.access_token;
          if (!token) {
            throw new Error('No access token returned from login');
          }
          this.localStorageService.setAccessToken(token);
          localStorage.setItem(
            'granted_scopes',
            JSON.stringify(res?.data?.scopes || []),
          );
          localStorage.setItem('access_token_stored_at', '1769509056968');
          localStorage.setItem('expires_at', '1769515358002');
        }),
        switchMap(() => this.fetchAndStoreUser()),
      );
  }

  private fetchAndStoreUser(): Observable<any> {
    return this.http.get<any>(`${this.posApiUrl}/user`).pipe(
      tap((res) => {
        const user = res?.data;
        if (!user) {
          throw new Error('User info not found');
        }
        user.sub = user.id;
        localStorage.setItem('id_token_claims_obj', JSON.stringify(user));
      }),
    );
  }

  logout(): void {
    console.log('Performing logout in BasicAuthService');
    this.store.dispatch(resetGlobalRulesStates());
    this.permissionService.clearPermissions();
    localStorage.clear();
    this.router.navigate(['/login']);
  }

  clearSession(): void {
    this.store.dispatch(resetGlobalRulesStates());
    this.permissionService.clearPermissions();
    [
      'access_token',
      'access_token_stored_at',
      'expires_at',
      'granted_scopes',
      'id_token_claims_obj',
      'logged_in_id',
      'refresh_token',
    ].forEach((key) => localStorage.removeItem(key));
  }

  refreshToken(): Observable<void> {
    return new Observable<void>();
  }

  canLogin(): Observable<boolean> {
    return of(this.hasValidAccessToken());
  }

  isLoggedIn(): boolean {
    return this.hasValidAccessToken();
  }

  hasValidAccessToken(): boolean {
    const accessToken = this.localStorageService.getAccessToken();
    return !!accessToken;
  }
  getLoggedInId(): string {
    try {
      const raw = localStorage.getItem('id_token_claims_obj');
      if (!raw) return '-1';
      const obj: any = JSON.parse(raw);
      return obj?.sub || obj?.id || '-1';
    } catch {
      return '-1';
    }
  }
  getLoggedInName(): string {
    try {
      const raw = localStorage.getItem('id_token_claims_obj');
      if (!raw) return 'CS';
      const obj: any = JSON.parse(raw);
      return obj?.name ?? 'CS';
    } catch {
      return 'CS';
    }
  }
}
